import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Thermometer, Wind, Droplets, Telescope, Cloud, CloudRain } from "lucide-react";
import { format, parseISO } from "date-fns";

type HourlyDataPoint = {
    time: string;
    temperatureC: number;
    precipitationProbability: number;
    windSpeedMs: number;
    windDirectionDeg: number;
    weatherCode: number;
    conditionSummary: string;
};

type WeatherData = {
    hourly: HourlyDataPoint[];
    metadata?: {
        units?: {
            temperature?: string;
            wind_speed?: string;
        };
    };
};

type WeatherProps = {
    data: WeatherData;
};

// Helper to group hourly data by day
function groupByDay(hourlyData: HourlyDataPoint[]) {
    const days = new Map<string, HourlyDataPoint[]>();

    hourlyData.forEach(point => {
        const date = format(parseISO(point.time), 'yyyy-MM-dd');
        if (!days.has(date)) {
            days.set(date, []);
        }
        days.get(date)!.push(point);
    });

    return days;
}

// Helper to get daily summary from hourly data
function getDailySummary(hourlyPoints: HourlyDataPoint[]) {
    const temps = hourlyPoints.map(p => p.temperatureC);
    const high = Math.max(...temps);
    const low = Math.min(...temps);

    // Use the most common condition summary
    const conditions = hourlyPoints.map(p => p.conditionSummary);
    const mostCommon = conditions.sort((a, b) =>
        conditions.filter(c => c === a).length - conditions.filter(c => c === b).length
    ).pop() || conditions[0];

    // Average precipitation probability
    const avgPrecip = Math.round(
        hourlyPoints.reduce((sum, p) => sum + p.precipitationProbability, 0) / hourlyPoints.length
    );

    // Average wind speed (convert m/s to km/h)
    const avgWind = Math.round(
        (hourlyPoints.reduce((sum, p) => sum + p.windSpeedMs, 0) / hourlyPoints.length) * 3.6
    );

    return { high, low, conditions: mostCommon, avgPrecip, avgWind };
}

export function Weather({ data }: WeatherProps) {
    const todayData = data.hourly.slice(0, 24); // First 24 hours
    const todaySummary = getDailySummary(todayData);

    // Group all data by day for weekly view
    const dayGroups = groupByDay(data.hourly);
    const weeklyData = Array.from(dayGroups.entries()).map(([date, points]) => ({
        date,
        summary: getDailySummary(points),
        points
    }));

    return (
        <Card>
            <CardHeader>
                <CardTitle>Weather Forecast</CardTitle>
            </CardHeader>
            <CardContent>
                <Tabs defaultValue="today" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="today">Today</TabsTrigger>
                        <TabsTrigger value="hourly">Hourly</TabsTrigger>
                        <TabsTrigger value="weekly">Weekly</TabsTrigger>
                    </TabsList>

                    <TabsContent value="today" className="space-y-4 mt-4">
                        <div className="p-3 sm:p-4 bg-muted rounded-lg text-center">
                            <p className="text-base sm:text-lg font-semibold">{todaySummary.conditions}</p>
                            <p className="text-3xl sm:text-4xl font-bold">{todaySummary.high.toFixed(0)}°C</p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <Thermometer className="h-5 w-5 text-accent" />
                                <div>
                                    <p className="font-semibold">Low</p>
                                    <p className="text-muted-foreground">{todaySummary.low.toFixed(0)}°C</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Wind className="h-5 w-5 text-accent" />
                                <div>
                                    <p className="font-semibold">Wind</p>
                                    <p className="text-muted-foreground">{todaySummary.avgWind} km/h</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <CloudRain className="h-5 w-5 text-accent" />
                                <div>
                                    <p className="font-semibold">Rain</p>
                                    <p className="text-muted-foreground">{todaySummary.avgPrecip}%</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Cloud className="h-5 w-5 text-accent" />
                                <div>
                                    <p className="font-semibold">Conditions</p>
                                    <p className="text-muted-foreground text-xs">{todaySummary.conditions}</p>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="hourly" className="mt-4">
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {data.hourly.slice(0, 48).map((point, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg text-sm">
                                    <div className="flex items-center gap-3 flex-1">
                                        <span className="font-semibold w-16">
                                            {format(parseISO(point.time), 'HH:mm')}
                                        </span>
                                        <span className="text-muted-foreground flex-1 truncate">
                                            {point.conditionSummary}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            <CloudRain className="h-4 w-4 text-blue-500" />
                                            <span className="text-xs">{point.precipitationProbability}%</span>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <Wind className="h-4 w-4 text-gray-500" />
                                            <span className="text-xs">{Math.round(point.windSpeedMs * 3.6)} km/h</span>
                                        </div>

                                        <span className="font-bold w-12 text-right">
                                            {point.temperatureC.toFixed(0)}°C
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="weekly" className="mt-4">
                        <div className="space-y-2">
                            {weeklyData.map((day, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                                    <div className="flex-1">
                                        <p className="font-semibold">
                                            {format(parseISO(day.date), 'EEEE, MMM d')}
                                        </p>
                                        <p className="text-sm text-muted-foreground">{day.summary.conditions}</p>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            <CloudRain className="h-4 w-4 text-blue-500" />
                                            <span className="text-sm">{day.summary.avgPrecip}%</span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">
                                                {day.summary.low.toFixed(0)}°
                                            </span>
                                            <div className="w-16 h-2 bg-gradient-to-r from-blue-400 to-orange-400 rounded-full" />
                                            <span className="text-sm font-semibold">
                                                {day.summary.high.toFixed(0)}°C
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}
